/**
 * Pure per-slot derivation for the skill cluster (no DOM): which abilities glow (procs),
 * which slot shows "interrupt ready", and the final slot view built from the cooldown math
 * (kit/slotState.ts) plus the sim's canCast answer and the cast / queue / prediction flags.
 *
 * Two highlight tiers, all read from content data (no per-class tables):
 *  - **Spell activation** (animated border, WoW's proc glow). The rotation-rework rules (F11),
 *    mirrored from the sim's `activeHighlights` by `HighlightIndex` without allocating:
 *    an aura's explicit `ui.glow` (at `atStacks`), a `ui.proc` aura's ability-scoped mods, both ids
 *    of an active transform, and the player's own debuffs on the target with an explicit glow.
 *    Legacy content (auras with no `ui` / `overrides`) still lights through `EmpowerIndex`: a
 *    temporary buff whose mods empower the ability.
 *  - **Lit** (soft static rim): a top-level `conditional` whose condition holds now (Lava Burst:
 *    target has the player's Flame Shock; `selfHasAura.minStacks` honoured), or a `usableIf`
 *    ability that is usable right now (Execute below 20%).
 *
 * Everything writes into caller-owned objects; lookups cache per aura / ability id on first
 * sight only, so the per-frame path allocates nothing in steady state.
 */
import type { AbilityDef, AuraDef, CastResult, Condition, DeepReadonly, EffectSpec, Unit, UnitId, World } from '@keystone/sim';
import { type CooldownInput, type SlotView, computeCooldown, usableFromReason } from '../kit/slotState';

type Ability = DeepReadonly<AbilityDef> | AbilityDef;
type AuraDefR = DeepReadonly<AuraDef> | AuraDef;
type UnitR = DeepReadonly<Unit>;

/** What can light up one ability (built once per ability). */
export interface AbilityProcSpec {
  /** Player auras that power it (conditional `selfHasAura`); `selfMinStacks` parallel array. */
  selfAuras: readonly string[];
  selfMinStacks: readonly number[];
  /** Target auras that power it (conditional `targetHasAura`); `fromSelf` / `targetMinStacks` parallel arrays. */
  targetAuras: readonly string[];
  targetFromSelf: readonly boolean[];
  targetMinStacks: readonly number[];
  /** Has a `usableIf` gate: glows whenever it is usable (Execute). */
  gated: boolean;
  /** Interrupts (tag or effect). */
  interrupt: boolean;
}

interface CondAcc {
  self: string[];
  selfMin: number[];
  tgt: string[];
  from: boolean[];
  tgtMin: number[];
}

function collectCond(c: DeepReadonly<Condition> | Condition, acc: CondAcc): void {
  switch (c.type) {
    case 'selfHasAura':
      acc.self.push(c.auraId);
      acc.selfMin.push(Math.max(1, c.minStacks ?? 1));
      break;
    case 'targetHasAura':
      acc.tgt.push(c.auraId);
      acc.from.push(!!c.fromSelf);
      acc.tgtMin.push(Math.max(1, c.minStacks ?? 1));
      break;
    case 'and':
      for (const x of c.all) collectCond(x, acc);
      break;
    default:
      break; // chance / hp / or / not: not a reliable "lit" signal
  }
}

export function abilityProcSpec(a: Ability): AbilityProcSpec {
  const acc: CondAcc = { self: [], selfMin: [], tgt: [], from: [], tgtMin: [] };
  let interrupt = !!a.ui?.tags?.includes('interrupt');
  for (const e of a.effects as readonly DeepReadonly<EffectSpec>[]) {
    if (e.type === 'interrupt') interrupt = true;
    else if (e.type === 'conditional') collectCond(e.cond, acc);
  }
  return {
    selfAuras: acc.self,
    selfMinStacks: acc.selfMin,
    targetAuras: acc.tgt,
    targetFromSelf: acc.from,
    targetMinStacks: acc.tgtMin,
    gated: !!a.usableIf,
    interrupt,
  };
}

const EMPOWER_STATS: Readonly<Record<string, (v: number) => boolean>> = {
  castTimePct: (v) => v < 0,
  damageDonePct: (v) => v > 0,
  healingDonePct: (v) => v > 0,
  critChanceBonus: (v) => v > 0,
  resourceCostPct: (v) => v < 0,
};

/**
 * Ability ids a helpful aura empowers through ability-scoped mods (empty = none). Legacy path:
 * an aura that carries rework HUD metadata (`ui` / `overrides`) is lit by HighlightIndex instead,
 * so `ui.glow: false` (Master of the Elements) really stays dark.
 */
export function empoweredAbilities(def: AuraDefR | undefined): readonly string[] {
  if (!def || def.kind !== 'buff' || def.flags?.hidden || !def.mods || hasReworkUi(def)) return NONE;
  let out: string[] | null = null;
  for (const m of def.mods) {
    const ok = EMPOWER_STATS[m.stat];
    if (!ok || !m.abilityIds?.length || !ok(m.value)) continue;
    for (const id of m.abilityIds) {
      out ??= [];
      if (!out.includes(id)) out.push(id);
    }
  }
  return out ?? NONE;
}
const NONE: readonly string[] = [];

/** Caches `empoweredAbilities` per aura id (aura defs never change during a run). */
export class EmpowerIndex {
  private readonly cache = new Map<string, readonly string[]>();
  constructor(private readonly auraDef: (id: string) => AuraDefR | undefined) {}

  /**
   * Stacks of the strongest temporary buff on `unit` that empowers `abilityId` (0 = none).
   * Permanent auras (talent / gem passives) never count: they are always on, not a proc.
   */
  stacks(unit: UnitR | undefined, abilityId: string): number {
    if (!unit) return 0;
    let best = 0;
    for (const inst of unit.auras) {
      if (inst.expiresAt === null) continue;
      let ids = this.cache.get(inst.auraId);
      if (!ids) this.cache.set(inst.auraId, (ids = empoweredAbilities(this.auraDef(inst.auraId))));
      if (ids.length && ids.includes(abilityId)) best = Math.max(best, inst.stacks || 1);
    }
    return best;
  }
}

function hasAura(u: UnitR | undefined, auraId: string, casterId: string | null, minStacks: number): boolean {
  if (!u) return false;
  for (const a of u.auras) if (a.auraId === auraId && (casterId === null || a.casterId === casterId) && (a.stacks || 1) >= minStacks) return true;
  return false;
}

/** Does a conditional / gate on the ability hold right now? (`minStacks` thresholds honoured.) */
export function conditionLit(spec: AbilityProcSpec, self: UnitR | undefined, target: UnitR | undefined, usableNow: boolean): boolean {
  if (spec.gated && usableNow) return true;
  for (let i = 0; i < spec.selfAuras.length; i++) if (hasAura(self, spec.selfAuras[i]!, null, spec.selfMinStacks[i] ?? 1)) return true;
  for (let i = 0; i < spec.targetAuras.length; i++) {
    if (hasAura(target, spec.targetAuras[i]!, spec.targetFromSelf[i] ? (self?.id ?? null) : null, spec.targetMinStacks[i] ?? 1)) return true;
  }
  return false;
}

// ---- rotation rework (F2 / F11): activation glow, transforms, stack counters --------------

/** Does the aura carry rework HUD metadata (then the F11 rules, not the legacy mod scan, decide its glow)? */
export function hasReworkUi(def: AuraDefR): boolean {
  return !!def.ui || !!def.overrides?.length;
}

/** Per-aura HUD facts, built once per aura id. */
export interface AuraHud {
  kind: 'buff' | 'debuff';
  /** `ui.glow === false`: no glow at all from this aura (not even its transform). */
  suppressed: boolean;
  /** Explicit `ui.glow.abilityIds` (null = none). */
  explicit: readonly string[] | null;
  atStacks: number;
  /** `ui.proc` without an explicit glow: every ability its mods are scoped to. */
  auto: readonly string[];
  /** Flattened override pairs [base0, form0, base1, form1, …]. */
  overrides: readonly string[];
  counterOn: readonly string[];
  proc: boolean;
  /** Shown in the cluster's proc row: a proc, a transform offer or a stack the buttons count. */
  procRow: boolean;
}

const EMPTY_HUD: AuraHud = { kind: 'buff', suppressed: true, explicit: null, atStacks: 1, auto: NONE, overrides: NONE, counterOn: NONE, proc: false, procRow: false };

export function auraHud(def: AuraDefR | undefined): AuraHud {
  if (!def || !hasReworkUi(def)) return def ? { ...EMPTY_HUD, kind: def.kind } : EMPTY_HUD;
  const glow = def.ui?.glow;
  const auto: string[] = [];
  if (!glow && def.ui?.proc) for (const m of def.mods ?? []) for (const id of m.abilityIds ?? []) if (!auto.includes(id)) auto.push(id);
  const overrides: string[] = [];
  for (const o of def.overrides ?? []) overrides.push(o.abilityId, o.with);
  const counterOn = (def.ui?.stackCounterOn ?? NONE) as readonly string[];
  const proc = !!def.ui?.proc;
  return {
    kind: def.kind,
    suppressed: glow === false,
    explicit: glow ? (glow.abilityIds as readonly string[]) : null,
    atStacks: glow ? Math.max(1, glow.atStacks ?? 1) : 1,
    auto,
    overrides,
    counterOn,
    proc,
    procRow: def.kind === 'buff' && !def.flags?.hidden && (proc || overrides.length > 0 || counterOn.length > 0),
  };
}

/** Cluster proc row: proc auras, transform offers and buffs whose stacks a button counts. Cheap (called per aura per frame). */
export function isProcRowAura(def: AuraDefR | undefined): boolean {
  return !!def && def.kind === 'buff' && !def.flags?.hidden && (!!def.ui?.proc || !!def.overrides?.length || !!def.ui?.stackCounterOn?.length);
}

type AuraInstR = UnitR['auras'][number];

/**
 * Spell-activation glow + stack counters for one unit, recomputed per frame into reused
 * containers (no allocation in steady state). Same rules as the sim's `activeHighlights` /
 * `stackCounters` (packages/sim core/hotbar.ts), parity-tested in derive.test.ts.
 */
export class HighlightIndex {
  /** Ability ids that glow now (base ids of transformed buttons included). */
  readonly glow = new Set<string>();
  /** Stack count per ability id (`ui.stackCounterOn`), base ids of transformed buttons included. */
  readonly counters = new Map<string, number>();
  private readonly cache = new Map<string, AuraHud>();
  constructor(private readonly auraDef: (id: string) => AuraDefR | undefined) {}

  hud(auraId: string): AuraHud {
    let h = this.cache.get(auraId);
    if (!h) this.cache.set(auraId, (h = auraHud(this.auraDef(auraId))));
    return h;
  }

  /**
   * Recompute for `unitId`. `targetId` is the target whose debuffs count (the HUD passes the
   * selected target so it reacts on the click frame; the sim uses `unit.targetId`).
   */
  compute(world: DeepReadonly<World>, unitId: UnitId, targetId: UnitId | null | undefined): this {
    const glow = this.glow;
    const counters = this.counters;
    glow.clear();
    counters.clear();
    const u = world.units[unitId];
    if (!u?.alive) return this;
    for (const inst of u.auras) {
      const h = this.hud(inst.auraId);
      if (h.kind === 'buff') this.count(h, inst);
      if (h.suppressed) continue;
      this.light(h, inst, true);
    }
    const t = targetId ? world.units[targetId] : undefined;
    if (t?.alive && t.id !== u.id) {
      for (const inst of t.auras) {
        if (inst.casterId !== unitId) continue;
        const h = this.hud(inst.auraId);
        if (h.kind === 'debuff') this.count(h, inst);
        if (!h.suppressed && h.explicit) this.light(h, inst, false);
      }
    }
    const ov = u.abilityOverrides;
    if (ov) {
      for (const base in ov) {
        const form = ov[base]!;
        if (glow.has(form)) glow.add(base);
        const c = counters.get(form);
        if (c !== undefined && !counters.has(base)) counters.set(base, c);
      }
    }
    return this;
  }

  private light(h: AuraHud, inst: AuraInstR, own: boolean): void {
    const glow = this.glow;
    if (h.explicit) {
      if (inst.stacks >= h.atStacks) for (const id of h.explicit) glow.add(id);
    } else if (own && h.proc) for (const id of h.auto) glow.add(id);
    if (own) for (const id of h.overrides) glow.add(id);
  }

  private count(h: AuraHud, inst: AuraInstR): void {
    for (const id of h.counterOn) this.counters.set(id, (this.counters.get(id) ?? 0) + inst.stacks);
  }
}

/** The form a press of `baseId` casts right now (the base id when not transformed, or the form is unknown). */
export function shownAbilityId(u: UnitR | undefined, baseId: string, known: (id: string) => boolean): string {
  const o = u?.abilityOverrides?.[baseId];
  return o !== undefined && known(o) ? o : baseId;
}

/** The selected enemy is casting something this interrupt can stop. */
export function interruptWanted(spec: AbilityProcSpec, self: UnitR | undefined, target: UnitR | undefined): boolean {
  return spec.interrupt && !!self && !!target && target.alive && target.faction !== self.faction && !!target.cast?.interruptible;
}

/** A cluster slot's frame: the kit SlotView plus the cluster highlights. */
export interface ClusterSlotView extends SlotView {
  /** Any highlight (activation or lit). */
  proc: boolean;
  /** Spell activation: a proc empowers this button / it is transformed (animated border). */
  activation: boolean;
  /** Soft "condition holds" rim (conditional / usableIf), only when not activated. */
  lit: boolean;
  /** The button currently casts its transformed form (icon / name swapped). */
  transformed: boolean;
  /** Stacks of the empowering buff (rank label "×N" when > 1). */
  procStacks: number;
  /** Stack counter (`ui.stackCounterOn`), shown whether or not the button is ready (0 = none). */
  counter: number;
  /** Interrupt-ready highlight. */
  kick: boolean;
}

export function emptyClusterSlotView(): ClusterSlotView {
  return {
    cdFrac: 0,
    cdMs: 0,
    onCooldown: false,
    gcdFrac: 0,
    charges: 0,
    maxCharges: 1,
    usable: 'ok',
    casting: false,
    queued: false,
    proc: false,
    activation: false,
    lit: false,
    transformed: false,
    procStacks: 0,
    counter: 0,
    kick: false,
  };
}

export interface SlotDeriveInput {
  cooldown: CooldownInput;
  /** queries.canCast for the resolved target (null when the query threw). */
  check: CastResult | null;
  casting: boolean;
  queued: boolean;
  /** Stacks of an empowering buff (EmpowerIndex.stacks; legacy auras without rework ui). */
  empowerStacks: number;
  /** conditionLit(...) for this ability. */
  conditionLit: boolean;
  /** interruptWanted(...) for this ability. */
  interruptWanted: boolean;
  /** HighlightIndex.glow has the bound or the shown id. */
  highlighted?: boolean;
  /** HighlightIndex.counters for the bound id (0 = none). */
  stackCounter?: number;
  /** The shown id differs from the bound one. */
  transformed?: boolean;
}

/** Fill `out` for one slot this frame. */
export function deriveSlot(i: SlotDeriveInput, out: ClusterSlotView): ClusterSlotView {
  computeCooldown(i.cooldown, out);
  out.usable = !i.check || i.check.ok ? 'ok' : usableFromReason(false, i.check.reason);
  out.casting = i.casting;
  out.queued = i.queued;
  // A slot that cannot fire now (locked by its cooldown, or hard-unusable) never glows.
  // (Charge abilities with a charge left are not `onCooldown`.)
  const ready = !out.onCooldown && out.usable !== 'unusable';
  out.procStacks = ready ? i.empowerStacks : 0;
  out.activation = ready && (!!i.highlighted || i.empowerStacks > 0);
  out.lit = ready && !out.activation && i.conditionLit;
  out.proc = out.activation || out.lit;
  out.transformed = !!i.transformed;
  out.counter = i.stackCounter ?? 0;
  // Interrupt-ready also shows out of range (the key flashes red: move closer), not on cooldown.
  out.kick = i.interruptWanted && !out.onCooldown && out.usable !== 'unusable';
  return out;
}

/** Rank label of a slot: the stack counter ("3") wins over the empowering-buff stacks ("×2"). */
export function slotRankLabel(v: Pick<ClusterSlotView, 'counter' | 'procStacks'>): string {
  return v.counter > 0 ? counterLabel(v.counter) : stackLabel(v.procStacks);
}
const COUNTER_LABEL: readonly string[] = Array.from({ length: 21 }, (_, n) => (n > 0 ? String(n) : ''));
export const counterLabel = (n: number): string => (n > 0 ? (COUNTER_LABEL[n] ?? String(n)) : '');

/** "×2" … "×9" (interned). */
const STACK_LABEL: readonly string[] = Array.from({ length: 10 }, (_, n) => (n > 1 ? `×${n}` : ''));
export const stackLabel = (n: number): string => (n > 1 ? (STACK_LABEL[n] ?? `×${n}`) : '');

/** Cast-bar time key in tenths of a second (0 = none). */
export function castTenths(ms: number): number {
  return ms > 0 ? Math.max(1, Math.round(ms / 100)) : 0;
}
